import { Tooltip } from '@heroui/react';
import { useQueryClient } from '@tanstack/react-query';
import { Unlink } from 'lucide-react';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AddButton, ModalConfirmation } from '@/components';
import { useHeroToast, useWarmup } from '@/hooks';
import { useOrganizationState } from '@/state';
import { useGetAllRoomServices } from '@/views/RoomService/queries';

import {
  isKdsDeviceOnline, kdsDevicesQueryKey, useGetKdsDevices, useUnpairKdsDevice,
} from '../../queries';
import { PairKdsDeviceModal } from '../PairKdsDeviceModal';
import * as S from './KdsDevicesSection.styles';

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/**
 * Lista ekranów KDS (tabletów kuchennych) hotelu wraz z parowaniem i odłączaniem.
 * Osobna sekcja od POD-ów, bo to inna klasa urządzeń: nie stoją w pokojach, nie
 * mają treści do edycji, a ich cały cykl życia to sparuj → pracuj → odłącz.
 */
export const KdsDevicesSection: FC = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { showError, showSuccess } = useHeroToast();
  const { organizationId } = useOrganizationState();

  useWarmup(['v3-listKdsDevices', 'v3-claimKdsDevice']);

  const [isPairOpen, setIsPairOpen] = useState(false);
  const [deviceToUnpair, setDeviceToUnpair] = useState<string | null>(null);

  const { data: devices = [], isLoading } = useGetKdsDevices(organizationId);
  const { data: roomServices = [] } = useGetAllRoomServices(organizationId);
  const unpairDevice = useUnpairKdsDevice();

  const outletNameById = useMemo(
    () => new Map(roomServices.map((outlet) => [outlet.id, outlet.name])),
    [roomServices],
  );

  const refreshDevices = () => {
    queryClient.invalidateQueries({ queryKey: kdsDevicesQueryKey(organizationId) });
  };

  const formatLastSeen = (lastSeenAt: number | null): string => {
    if (!lastSeenAt) return t('myDevices.kds.neverSeen', 'nigdy');
    const elapsed = Date.now() - lastSeenAt;
    if (elapsed < MINUTE) return t('myDevices.kds.justNow', 'przed chwilą');
    if (elapsed < HOUR) {
      return t('myDevices.kds.minutesAgo', '{{count}} min temu', {
        count: Math.floor(elapsed / MINUTE),
      });
    }
    if (elapsed < DAY) {
      return t('myDevices.kds.hoursAgo', '{{count}} godz. temu', {
        count: Math.floor(elapsed / HOUR),
      });
    }
    return new Date(lastSeenAt).toLocaleString();
  };

  const handleUnpair = () => {
    if (!deviceToUnpair || !organizationId) return;
    unpairDevice.mutate(
      { hotelId: organizationId, deviceId: deviceToUnpair },
      {
        onSuccess: () => {
          showSuccess(t('myDevices.kds.unpairSuccess', 'Ekran został odłączony.'));
          setDeviceToUnpair(null);
          refreshDevices();
        },
        onError: () => {
          showError(t('myDevices.kds.errors.unpairFailed', 'Nie udało się odłączyć ekranu.'));
          setDeviceToUnpair(null);
        },
      },
    );
  };

  if (!organizationId) return null;

  return (
    <S.Section>
      <S.Header>
        <div>
          <S.Title>{t('myDevices.kds.sectionTitle', 'Ekrany KDS')}</S.Title>
          <S.Subtitle>
            {t(
              'myDevices.kds.sectionDescription',
              'Tablety w kuchni, na których obsługa widzi zamówienia room service i zmienia ich statusy.',
            )}
          </S.Subtitle>
        </div>
        <AddButton
          isSmall
          label={String(t('myDevices.kds.addDevice', 'Dodaj ekran'))}
          onClick={() => setIsPairOpen(true)}
        />
      </S.Header>

      <S.TableWrap>
        <S.Table>
          <thead>
            <tr>
              <th>{t('myDevices.kds.columnName', 'Nazwa')}</th>
              <th style={{ width: 200 }}>{t('myDevices.kds.columnOutlet', 'Punkt gastronomiczny')}</th>
              <th style={{ width: 130 }}>{t('myDevices.kds.columnStatus', 'Status')}</th>
              <th style={{ width: 160 }}>{t('myDevices.kds.columnLastSeen', 'Ostatni kontakt')}</th>
              <th style={{ width: 60 }} />
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr>
                <td colSpan={5} className="muted">
                  {t('common.loading', 'Ładowanie...')}
                </td>
              </tr>
            )}

            {!isLoading && devices.length === 0 && (
              <tr>
                <td colSpan={5} style={{ padding: 0 }}>
                  <S.EmptyContent>
                    <S.EmptyTitle>
                      {t('myDevices.kds.emptyTitle', 'Brak sparowanych ekranów KDS')}
                    </S.EmptyTitle>
                    <S.EmptyDescription>
                      {t(
                        'myDevices.kds.emptyDescription',
                        'Uruchom aplikację KDS na tablecie i przypisz go do hotelu kodem, który pokaże się na jego ekranie.',
                      )}
                    </S.EmptyDescription>
                  </S.EmptyContent>
                </td>
              </tr>
            )}

            {!isLoading && devices.map((device) => {
              const online = isKdsDeviceOnline(device.lastSeenAt);
              return (
                <tr key={device.deviceId}>
                  <td>
                    <S.DeviceName>
                      {device.name || t('myDevices.kds.unnamed', 'Ekran KDS')}
                    </S.DeviceName>
                    {device.model && <S.DeviceMeta>{device.model}</S.DeviceMeta>}
                  </td>
                  <td className="muted">
                    {device.roomServiceId
                      ? outletNameById.get(device.roomServiceId) ?? device.roomServiceId
                      : t('myDevices.kds.outletAll', 'Wszystkie punkty')}
                  </td>
                  <td>
                    <S.StatusPill $online={online}>
                      {online
                        ? t('myDevices.kds.online', 'Online')
                        : t('myDevices.kds.offline', 'Offline')}
                    </S.StatusPill>
                  </td>
                  <td className="muted">{formatLastSeen(device.lastSeenAt)}</td>
                  <td className="right">
                    <Tooltip
                      content={t('myDevices.kds.unpair', 'Odłącz ekran')}
                      showArrow
                      radius="sm"
                      size="sm"
                      delay={300}
                      classNames={{
                        base: 'pointer-events-auto',
                        content: 'bg-white dark:bg-[#242a33] text-gray-900 dark:text-gray-100 border border-gray-200 dark:border-[#374151] shadow-lg',
                      }}
                    >
                      <span>
                        <S.IconButton
                          type="button"
                          onClick={() => setDeviceToUnpair(device.deviceId)}
                          aria-label={String(t('myDevices.kds.unpair', 'Odłącz ekran'))}
                        >
                          <Unlink />
                        </S.IconButton>
                      </span>
                    </Tooltip>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </S.Table>
      </S.TableWrap>

      <PairKdsDeviceModal
        isOpen={isPairOpen}
        hotelId={organizationId}
        onClose={() => setIsPairOpen(false)}
        onPaired={refreshDevices}
      />

      <ModalConfirmation
        isOpen={!!deviceToUnpair}
        isLoading={unpairDevice.isPending}
        itemId={deviceToUnpair}
        onClose={() => setDeviceToUnpair(null)}
        onConfirm={handleUnpair}
        title={String(t('myDevices.kds.unpairTitle', 'Odłączyć ekran KDS?'))}
        description={t(
          'myDevices.kds.unpairDescription',
          'Tablet natychmiast straci dostęp do zamówień. Aby wrócić do pracy, będzie musiał sparować się od nowa.',
        )}
        buttonConfirm={String(t('myDevices.kds.unpair', 'Odłącz ekran'))}
      />
    </S.Section>
  );
};
